import React, { useState } from "react";
import { HashLink } from 'react-router-hash-link';

import SiteHeader from '../SiteHeader';
import SiteFooter from '../SiteFooter';
import './Contact.css';

// Same sender as client contact forms (Acsence…): the backend knows this account's
// password and only lets it send to info@diamta.com, so nothing secret is in the page.
const MAIL_API_URL 	= 'https://mail.diamta.com/backend/diamta-mail/email/send';
const MAIL_USER 	= 'courrier@diamta.com';
const MAIL_TO 		= [ 'info@diamta.com' ];

const topics = [
	'General question',
	'Business email / my own domain',
	'Technical help',
	'Billing',
	'Other',
];

const emptyForm = { name: '', email: '', topic: topics[ 0 ], message: '', website: '' };

const escapeHtml = ( text ) => String( text )
	.replace( /&/g, '&amp;' )
	.replace( /</g, '&lt;' )
	.replace( />/g, '&gt;' )
	.replace( /"/g, '&quot;' );

const Contact = () => {
	const [ form, setForm ] 		= useState( emptyForm );
	const [ errors, setErrors ] 	= useState( {} );
	const [ sending, setSending ] 	= useState( false );
	const [ sent, setSent ] 		= useState( false );
	const [ sendError, setSendError ] = useState( '' );

	const handleChange = ( e ) => {
		const { name, value } = e.target;
		setForm( prev => ( { ...prev, [ name ]: value } ) );
		if( errors[ name ] )
			setErrors( prev => ( { ...prev, [ name ]: '' } ) );
	};

	const validate = () => {
		const next = {};
		if( !form.name.trim() )
			next.name = 'Please enter your name.';
		if( !/^\S+@\S+\.\S+$/.test( form.email.trim() ) )
			next.email = 'Please enter a valid email address so we can reply.';
		if( form.message.trim().length < 10 )
			next.message = 'Please write a few words about how we can help.';
		else if( form.message.length > 5000 )
			next.message = 'Your message is too long (5,000 characters at most).';
		return next;
	};

	const handleSubmit = async ( e ) => {
		e.preventDefault();
		setSendError( '' );
		const next = validate();
		setErrors( next );
		if( Object.keys( next ).length )
			return;

		// hidden field only robots fill in: pretend it worked
		if( form.website ){
			setSent( true );
			return;
		}

		const name = form.name.trim();
		const email = form.email.trim();
		const html = `<p>New message from the contact page of <strong>mail.diamta.com</strong>.</p>
			<table style="border-collapse:collapse;width:100%;max-width:600px">
				<tr><td style="padding:8px;font-weight:bold">Name</td><td style="padding:8px">${ escapeHtml( name ) }</td></tr>
				<tr style="background:#f6f7fb"><td style="padding:8px;font-weight:bold">Email</td><td style="padding:8px">${ escapeHtml( email ) }</td></tr>
				<tr><td style="padding:8px;font-weight:bold">Topic</td><td style="padding:8px">${ escapeHtml( form.topic ) }</td></tr>
				<tr style="background:#f6f7fb"><td style="padding:8px;font-weight:bold;vertical-align:top">Message</td><td style="padding:8px">${ escapeHtml( form.message.trim() ).replace( /\n/g, '<br/>' ) }</td></tr>
			</table>
			<p style="color:#4a5270">Reply to this email to answer ${ escapeHtml( name ) } directly.</p>`;

		setSending( true );
		try{
			const response = await fetch( MAIL_API_URL, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					auth_user: 	MAIL_USER,
					to: 		MAIL_TO,
					reply_to: 	email,
					subject: 	`[Diamta Mail contact] ${ form.topic } — ${ name }`.slice( 0, 200 ),
					html,
				}),
			});
			if( !response.ok ){
				const body = await response.json().catch( () => ({}) );
				throw new Error( body.message || 'Send failed' );
			}
			setSent( true );
			setForm( emptyForm );
		}
		catch( err ){
			setSendError( err.message === 'Too many messages. Please try again later.'
				? err.message
				: 'Your message could not be sent. Please try again, or write to us at info@diamta.com.' );
		}
		finally{
			setSending( false );
		}
	};

	return (
		<div className="dm-site dm-contact">
			<SiteHeader />

			<main>
				<section className="dm-contact-hero">
					<div className="dm-container">
						<p className="dm-contact-eyebrow">Contact</p>
						<h1>How can we help?</h1>
						<p className="dm-contact-lead">Questions about your mailboxes, using your own domain, or anything else: send us a message and we'll reply by email.</p>
					</div>
				</section>

				<div className="dm-container dm-contact-grid">
					<section className="dm-contact-card" aria-labelledby="dm-contact-form-title">
						{ sent ?
							<div className="dm-contact-sent" role="status">
								<span className="dm-contact-sent-icon" aria-hidden="true">
									<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12l5 5 9-10" /></svg>
								</span>
								<h2 id="dm-contact-form-title">Thanks, your message has been sent</h2>
								<p>We'll answer at the email address you gave us.</p>
								<button type="button" className="dm-btn dm-btn-outline" onClick={ () => setSent( false ) }>Send another message</button>
							</div>
						:
							<form onSubmit={ handleSubmit } noValidate>
								<h2 id="dm-contact-form-title">Send us a message</h2>

								<div className="dm-contact-row">
									<div className="dm-field">
										<label htmlFor="dm-c-name">Your name</label>
										<input id="dm-c-name" name="name" className="dm-input" value={ form.name } onChange={ handleChange } autoComplete="name"
											aria-invalid={ !!errors.name || undefined } aria-describedby={ errors.name ? 'dm-c-name-error' : undefined } />
										{ errors.name && <p className="dm-error" id="dm-c-name-error">{ errors.name }</p> }
									</div>
									<div className="dm-field">
										<label htmlFor="dm-c-email">Your email address</label>
										<input id="dm-c-email" name="email" type="email" className="dm-input" value={ form.email } onChange={ handleChange } autoComplete="email"
											aria-invalid={ !!errors.email || undefined } aria-describedby={ errors.email ? 'dm-c-email-error' : undefined } />
										{ errors.email && <p className="dm-error" id="dm-c-email-error">{ errors.email }</p> }
									</div>
								</div>

								<div className="dm-field">
									<label htmlFor="dm-c-topic">Topic</label>
									<select id="dm-c-topic" name="topic" className="dm-input" value={ form.topic } onChange={ handleChange }>
										{ topics.map( t => <option key={ t } value={ t }>{ t }</option> ) }
									</select>
								</div>

								<div className="dm-field">
									<label htmlFor="dm-c-message">Message</label>
									<textarea id="dm-c-message" name="message" className="dm-input dm-textarea" rows={ 7 } value={ form.message } onChange={ handleChange }
										aria-invalid={ !!errors.message || undefined } aria-describedby={ errors.message ? 'dm-c-message-error' : undefined } />
									{ errors.message && <p className="dm-error" id="dm-c-message-error">{ errors.message }</p> }
								</div>

								{/* spam trap: hidden from people, filled in by robots */}
								<div className="dm-contact-trap" aria-hidden="true">
									<label htmlFor="dm-c-website">Website</label>
									<input id="dm-c-website" name="website" tabIndex={ -1 } autoComplete="off" value={ form.website } onChange={ handleChange } />
								</div>

								{ sendError && <p className="dm-error dm-contact-send-error" role="alert">{ sendError }</p> }

								<button type="submit" className="dm-btn dm-btn-primary dm-btn-lg" disabled={ sending }>
									{ sending ? 'Sending…' : 'Send message' }
								</button>
								<p className="dm-help dm-contact-privacy">We only use your details to answer your message.</p>
							</form>
						}
					</section>

					<aside className="dm-contact-side">
						<div className="dm-contact-info">
							<span className="dm-contact-info-icon" aria-hidden="true">
								<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3 7l9 6 9-6" /></svg>
							</span>
							<div>
								<h2>Email</h2>
								<p><a href="mailto:info@diamta.com">info@diamta.com</a></p>
							</div>
						</div>
						<div className="dm-contact-info">
							<span className="dm-contact-info-icon" aria-hidden="true">
								<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="7" y="2" width="10" height="20" rx="2" /><path d="M11 18h2" /></svg>
							</span>
							<div>
								<h2>Setting up an email app?</h2>
								<p>The server settings for Outlook, Thunderbird and phones are on our <HashLink smooth to="/home#setup">email app setup</HashLink> section.</p>
							</div>
						</div>
						<div className="dm-contact-info">
							<span className="dm-contact-info-icon" aria-hidden="true">
								<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" /></svg>
							</span>
							<div>
								<h2>Email on your own domain</h2>
								<p>Tell us your domain name, or the one you'd like, and we'll help you set it up. See the <HashLink smooth to="/home#pricing">Business plans</HashLink>.</p>
							</div>
						</div>
					</aside>
				</div>
			</main>

			<SiteFooter />
		</div>
	);
};

export default Contact;
